import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { LoadingButton } from "~/components/loading-button";
import { SelectMenu } from "~/components/Form/components/SelectMenu/SelectMenu";
import { Option } from "~/components/Form/components/SelectMenu/SelectMenu";
import { MembersRolesList } from "~/components/Members/MembersRolesList";
import {
  BASE_MEMBERS_ROLES,
  GROUPED_MEMBERS_ROLES,
  PERMISSIONS,
} from "~/components/Members/const";
import { hasAnyBaseRoleSelected } from "~/components/Members/utils/membersUtils";
import { roleSpaceMember } from "~/lib/iam/roles/SpaceMember";

type FormValues = {
  selectedRoles: string[];
};

export type EditSpaceRoleDialogProps = {
  open: boolean;
  spaceName: string;
  spaceMrn: string;
  currentRoles: Array<{ mrn: string; title?: string }>;
  onClose: () => void;
  onSave: (spaceMrn: string, roles: Array<{ mrn: string }>) => void;
  onRemoveAccess: (spaceMrn: string) => void;
  isLoading?: boolean;
};

export function EditSpaceRoleDialog({
  open,
  spaceName,
  spaceMrn,
  currentRoles,
  onClose,
  onSave,
  onRemoveAccess,
  isLoading = false,
}: EditSpaceRoleDialogProps) {
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    watch,
    setValue,
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      selectedRoles: currentRoles
        .map((role) => role.mrn)
        .filter((mrn) => mrn !== roleSpaceMember.mrn),
    },
    mode: "onChange",
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const [isRolesDropdownOpen, setIsRolesDropdownOpen] = useState(false);
  const watchSelectedRoles = watch("selectedRoles");

  const onSubmit = (data: FormValues) => {
    onSave(
      spaceMrn,
      data.selectedRoles.map((role) => ({ mrn: role })),
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleRemoveAccess = () => {
    onRemoveAccess(spaceMrn);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: "background.default",
            position: "relative",
            overflow: "visible",
          },
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          pb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Space Access
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {spaceName}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ overflow: "visible" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select the space roles for this user. Base roles define broad access
          levels, while detailed permissions provide granular control.
        </Typography>

        <Button
          variant="text"
          color="primary"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
            setIsRolesDropdownOpen(true);
          }}
          startIcon={<AddIcon />}
          data-testid="add-roles-button"
          sx={{ mb: 2 }}
        >
          Add Roles
        </Button>

        <FormHelperText error>
          {errors.selectedRoles?.message || ""}
        </FormHelperText>

        <Controller
          name="selectedRoles"
          control={control}
          rules={{
            validate: (value) => {
              const isHasAnyBaseRoleSelected = hasAnyBaseRoleSelected(value);
              if (!isHasAnyBaseRoleSelected) {
                return "Must have at least one base role selected";
              }
              return true;
            },
          }}
          render={({ field, formState: { errors } }) => (
            <Box sx={{ position: "relative" }}>
              <SelectMenu
                isOpen={isRolesDropdownOpen}
                anchorRef={anchorEl}
                onClose={() => {
                  setAnchorEl(null);
                  setIsRolesDropdownOpen(false);
                }}
                maxHeight={208}
                options={GROUPED_MEMBERS_ROLES}
                onChange={(value) => {
                  field.onChange(value);
                }}
                multiple={true}
                value={field.value}
                variant="checkbox"
                error={!!errors.selectedRoles}
                helperText={errors.selectedRoles?.message || ""}
              />
            </Box>
          )}
        />

        <Box mt={3}>
          <MembersRolesList
            roles={watchSelectedRoles
              .map((roleMrn) => {
                const label = [...BASE_MEMBERS_ROLES, ...PERMISSIONS].find(
                  (role) => role.value === roleMrn,
                )?.label;

                return label ? { value: roleMrn, label } : null;
              })
              .filter((option): option is Option => option !== null)}
            onDelete={(roleMrn) => {
              setValue(
                "selectedRoles",
                watchSelectedRoles.filter((x) => x !== roleMrn),
                { shouldValidate: true, shouldDirty: true },
              );
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "space-between",
          px: 3,
          py: 2,
        }}
      >
        <Button
          variant="text"
          color="error"
          onClick={handleRemoveAccess}
          disabled={isLoading}
          sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
        >
          Remove Access
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isLoading}
            sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={isLoading}
            buttonText="Save"
            variant="contained"
            color="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading || !isDirty || !isValid}
            sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
}
